/**
 * The scanner.
 *
 * Scan a mould, see what it is, say what you are doing with it. Everything else
 * in this product exists to make this screen possible or to read back what it
 * records, and until now it was the one screen the web app did not have - the
 * catalog could describe every mould and nothing could move one.
 *
 * Three ideas hold it together:
 *
 * 1. **Places, not checkpoint codes.** The database records a scan against a
 *    coded point whose type is what makes the server change status and custody.
 *    Nobody thinks that way on a floor. This asks where you are and works out
 *    which checkpoint each action needs - see `place.ts`.
 *
 * 2. **The device confirms, not the network.** A store room is where the signal
 *    dies and also where the scanning happens. The scan is written locally and
 *    confirmed at once; getting it to the server is this app's problem, not the
 *    supervisor's.
 *
 * 3. **Recorded is not the same as visible.** A scan sitting on a device is real
 *    to the person who took it and invisible to everyone else. That distinction
 *    is said out loud on every confirmation, because the alternative is a
 *    manager looking at a board that quietly disagrees with the floor.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  CloudOff,
  Info,
  MapPin,
  RefreshCw,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input, Select, Textarea } from '../../components/ui/field.js';
import { Badge, Card, CardBody, Code, EmptyState } from '../../components/ui/primitives.js';
import { ConfirmDialog } from '../../components/ui/dialog.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useMouldByScannedCode } from '../../db/queries.js';
import { newId } from '../../lib/id.js';
import { normaliseScannedCode } from '@mouldtrack/shared';
import { CodeReader } from './CodeReader.js';
import { ScanResult } from './ScanResult.js';

import {
  ACTION_LABEL,
  actionsFor,
  parsePlaceKey,
  placeKey,
  placeOptions,
  readPlace,
  useCheckpoints,
  writePlace,
  type Place,
  type ScanAction,
} from './place.js';
import {
  discardScan,
  enqueueScan,
  flushScanQueue,
  retryScan,
  useScanQueue,
} from './scanQueue.js';

interface Confirmation {
  tone: 'sent' | 'queued' | 'error';
  headline: string;
  detail: string;
}

export function ScanPage() {
  const { user, allows } = useAuth();
  const mayScan = allows('create', 'scans');

  const { checkpoints, loading: loadingPlaces, stale } = useCheckpoints();
  const [place, setPlace] = useState<Place | null>(readPlace);
  const [code, setCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [dueBack, setDueBack] = useState('');
  const [busy, setBusy] = useState<ScanAction | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [discarding, setDiscarding] = useState<number | null>(null);

  const mould = useMouldByScannedCode(code ?? undefined);
  const queue = useScanQueue();
  const unsent = queue?.filter((s) => s.status !== 'failed').length ?? 0;
  const failed = queue?.filter((s) => s.status === 'failed') ?? [];

  const places = useMemo(() => placeOptions(checkpoints), [checkpoints]);
  const actions = useMemo(() => actionsFor(checkpoints, place), [checkpoints, place]);

  /**
   * Fall back to the only place there is.
   *
   * Most of this client's sites have exactly one scan point, and making somebody
   * choose from a list of one is a question with no information in it.
   */
  useEffect(() => {
    if (place || places.length !== 1 || !places[0]) return;
    const only = { locationId: places[0].locationId, storeId: places[0].storeId };
    setPlace(only);
    writePlace(only);
  }, [place, places]);

  // Anything queued gets another try whenever the connection returns.
  useEffect(() => {
    const onOnline = () => void flushScanQueue();
    window.addEventListener('online', onOnline);
    void flushScanQueue();
    return () => window.removeEventListener('online', onOnline);
  }, []);

  function choosePlace(key: string) {
    const next = parsePlaceKey(key);
    setPlace(next);
    writePlace(next);
  }

  function reset() {
    setCode(null);
    setNote('');
    setDueBack('');
  }

  async function record(action: ScanAction) {
    const resolved = actions.find((a) => a.action === action);
    if (!resolved?.checkpoint || !mould || !place) return;

    setBusy(action);
    setConfirmation(null);

    const placeLabel =
      places.find((p) => placeKey(p) === placeKey(place))?.label ??
      resolved.checkpoint.location?.name ??
      resolved.checkpoint.code;

    try {
      await enqueueScan({
        clientScanId: newId(),
        assetCode: mould.assetCode,
        checkpointCode: resolved.checkpoint.code,
        scannedAt: new Date().toISOString(),
        ...(note.trim() ? { remarks: note.trim() } : {}),
        // Only meaningful on a check-out. The server ignores it elsewhere, but
        // sending it anyway would put a return date in the ledger for a
        // movement that has no such thing.
        ...(action === 'out' && dueBack
          ? { expectedBackBy: new Date(`${dueBack}T00:00:00`).toISOString() }
          : {}),
        mouldName: mould.name,
        placeLabel,
        actionLabel: ACTION_LABEL[action],
      });

      const result = await flushScanQueue();

      const what =
        action === 'out'
          ? `${mould.assetCode} is checked out to ${user?.name ?? 'you'}`
          : action === 'in'
            ? `${mould.assetCode} is checked in at ${placeLabel}`
            : `${mould.assetCode} logged at ${placeLabel}`;

      if (result.failed > 0) {
        setConfirmation({
          tone: 'error',
          headline: 'The server refused this scan',
          detail: 'It is kept below with the reason, so nothing is lost.',
        });
      } else if (result.sent > 0) {
        setConfirmation({
          tone: 'sent',
          headline: `${what}.`,
          detail: 'Saved to the server - everyone can see it now.',
        });
      } else {
        setConfirmation({
          tone: 'queued',
          headline: `${what}.`,
          // The honest half. It is recorded, and it is not shared yet.
          detail:
            'Saved on this device. Nobody else can see it until this phone is back online.',
        });
      }
      reset();
    } catch {
      setConfirmation({
        tone: 'error',
        headline: 'Could not record that scan',
        detail: 'Nothing was saved. Try again.',
      });
    } finally {
      setBusy(null);
    }
  }

  if (!mayScan) {
    return (
      <Page title="Scan" description="Record where a mould is.">
        <EmptyState
          icon={<Info />}
          title="Your role does not record scans"
          description="Scanning is for the people who handle the tools. You can still see where everything is on Tracking."
          action={
            <Button variant="secondary" asChild>
              <Link to="/tracking">Open Tracking</Link>
            </Button>
          }
        />
      </Page>
    );
  }

  return (
    <Page
      title="Scan"
      description="Scan a mould to see what it is, then check it out, check it in, or log where it is."
      actions={
        unsent > 0 ? (
          <Button variant="secondary" onClick={() => void flushScanQueue()}>
            <RefreshCw className="size-4" /> Send {unsent}
          </Button>
        ) : null
      }
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
          {/* Where you are. First, because every action below depends on it and
              because getting it wrong records a real tool in the wrong place. */}
          <div className="mb-4 rounded-xl border border-border bg-surface p-4">
            <Field
              label="Where are you?"
              hint={
                stale && !loadingPlaces
                  ? 'Offline - showing the scan points this device last downloaded.'
                  : 'Remembered on this device, so you only pick it once.'
              }
            >
              <Select
                value={placeKey(place)}
                onChange={(e) => choosePlace(e.target.value)}
                placeholder={loadingPlaces ? 'Loading…' : 'Choose a site or store'}
                options={places.map((p) => ({ value: placeKey(p), label: p.label }))}
                aria-label="Where are you"
              />
            </Field>
            {!loadingPlaces && places.length === 0 ? (
              <p className="mt-2 rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
                No scan points are set up yet. An administrator needs to add at least
                one under Sites &amp; Scanning → Checkpoints before anything can be
                scanned.
              </p>
            ) : null}
          </div>

          <div className="mb-4">
            <CodeReader
              onCode={(raw) => {
                setConfirmation(null);
                setCode(normaliseScannedCode(raw));
              }}
              paused={Boolean(code)}
            />
          </div>

          {confirmation ? (
            <div
              role="status"
              className={
                confirmation.tone === 'sent'
                  ? 'mb-4 flex items-start gap-2 rounded-xl bg-success-subtle px-4 py-3 text-sm text-success-foreground'
                  : confirmation.tone === 'queued'
                    ? 'mb-4 flex items-start gap-2 rounded-xl bg-warning-subtle px-4 py-3 text-sm text-warning-foreground'
                    : 'mb-4 flex items-start gap-2 rounded-xl bg-danger-subtle px-4 py-3 text-sm text-danger'
              }
            >
              {confirmation.tone === 'sent' ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden />
              ) : confirmation.tone === 'queued' ? (
                <CloudOff className="mt-0.5 size-4 shrink-0" aria-hidden />
              ) : (
                <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
              )}
              <span>
                <strong>{confirmation.headline}</strong>{' '}
                <span className="opacity-90">{confirmation.detail}</span>
              </span>
            </div>
          ) : null}

          {code ? (
            <ScanResult code={code} mould={mould}>
              {mould ? (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="size-4" aria-hidden />
                    {place
                      ? places.find((p) => placeKey(p) === placeKey(place))?.label ?? 'Somewhere'
                      : 'Choose where you are, above'}
                  </div>

                  <div className="mb-3 grid gap-3 sm:grid-cols-2">
                    <Field label="Due back" hint="Optional, and only used on a check-out.">
                      <Input
                        type="date"
                        value={dueBack}
                        onChange={(e) => setDueBack(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                      />
                    </Field>
                    <Field label="Note" hint="Optional. Kept on the movement record.">
                      <Textarea
                        rows={1}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Anything worth recording…"
                      />
                    </Field>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {actions.map((a) => (
                      <div key={a.action} className="flex-1 basis-48">
                        <Button
                          className="w-full"
                          variant={a.action === 'here' ? 'secondary' : 'primary'}
                          disabled={!a.checkpoint || busy !== null}
                          loading={busy === a.action}
                          onClick={() => void record(a.action)}
                        >
                          {a.label}
                        </Button>
                        <p className="mt-1 text-xs text-subtle-foreground">
                          {a.unavailable ?? a.hint}
                        </p>
                      </div>
                    ))}
                  </div>

                  <Button variant="ghost" className="mt-3" onClick={reset}>
                    Scan something else
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={reset}>
                  Try another code
                </Button>
              )}
            </ScanResult>
          ) : null}
        </div>

        {/* Everything this device is still holding. Deliberately always on
            screen rather than behind a badge: an unsent scan is a movement the
            rest of the plant cannot see, and it should nag. */}
        <aside className="min-w-0">
          <Card>
            <CardBody>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">On this device</h2>
                {unsent > 0 ? <Badge tone="warning">{unsent} unsent</Badge> : null}
              </div>

              {!queue?.length ? (
                <p className="text-sm text-muted-foreground">
                  Nothing waiting. Every scan you have taken has reached the server
                  and is on{' '}
                  <Link to="/tracking" className="underline">
                    Tracking
                  </Link>{' '}
                  for everyone to see.
                </p>
              ) : (
                <ul className="space-y-2">
                  {queue.slice(0, 12).map((s) => (
                    <li
                      key={s.seq}
                      className="rounded-lg border border-border px-3 py-2 text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Code className="text-xs">{s.assetCode}</Code>
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {s.actionLabel} · {s.placeLabel}
                          </p>
                        </div>
                        <Badge tone={s.status === 'failed' ? 'danger' : 'warning'}>
                          {s.status === 'failed' ? 'Refused' : 'Waiting'}
                        </Badge>
                      </div>
                      {s.lastError ? (
                        <p className="mt-1 text-xs text-danger">{s.lastError}</p>
                      ) : null}
                      {s.status === 'failed' && s.seq !== undefined ? (
                        <div className="mt-2 flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => void retryScan(s.seq!).then(() => flushScanQueue())}
                          >
                            <RefreshCw className="size-3.5" /> Try again
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDiscarding(s.seq!)}
                          >
                            <Trash2 className="size-3.5" /> Discard
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}

              {failed.length > 0 ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  A refused scan is usually an unknown code, a scrapped mould, or a
                  checkpoint your account is not assigned to.
                </p>
              ) : null}
            </CardBody>
          </Card>
        </aside>
      </div>

      <ConfirmDialog
        open={discarding !== null}
        onOpenChange={(open) => !open && setDiscarding(null)}
        title="Discard this scan?"
        // Not a tidy-up. Somewhere a tool moved and this is the only record of it.
        description="The movement it recorded will not be kept anywhere. Only discard it if the scan was a mistake."
        confirmLabel="Discard"
        destructive
        onConfirm={async () => {
          if (discarding !== null) await discardScan(discarding);
          setDiscarding(null);
        }}
      />
    </Page>
  );
}
